/**
 * Utility functions for formatting data consistently across the application
 */

export interface FormatAmountOptions {
  currency?: string;
  currencyCode?: string;
  decimals?: number;
  locale?: string;
}

// Get user's locale from cookie or default to 'en'
function getUserLocale(): string {
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';');
    const localeCookie = cookies.find((cookie) =>
      cookie.trim().startsWith('i18next='),
    );
    if (localeCookie) {
      return localeCookie.split('=')[1];
    }
  }
  return 'en';
}

// Locale mapping for Intl formatters
const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  hu: 'hu-HU',
};

/**
 * Get currency symbol from currency code
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const currencyMap: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    HUF: 'Ft',
    JPY: '¥',
    CHF: 'CHF',
    CAD: 'C$',
    AUD: 'A$',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    CZK: 'Kč',
    RON: 'lei',
    BGN: 'лв',
    HRK: 'kn',
    RSD: 'дин',
  };

  return currencyMap[currencyCode] || currencyCode;
};

/**
 * Format monetary amounts with thousand separators
 */
export const formatAmount = (
  amount: number | string,
  options: FormatAmountOptions = {},
): string => {
  const userLocale = getUserLocale();
  const {
    currency = options.currencyCode
      ? getCurrencySymbol(options.currencyCode)
      : userLocale === 'hu'
      ? 'Ft'
      : '$',
    decimals = 0,
    locale = LOCALE_MAP[userLocale] || 'en-US',
  } = options;

  if (!amount && amount !== 0) return 'N/A';

  const numericAmount = parseFloat(amount.toString());
  if (isNaN(numericAmount)) return 'N/A';

  const formatted = numericAmount.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return currency ? `${currency}\u00A0${formatted}` : formatted;
};

/**
 * Format amounts with 2 decimal places (for detailed views)
 */
export const formatAmountDetailed = (amount: number | string, currency = 'Ft'): string => {
  return formatAmount(amount, {currency, decimals: 0});
};

/**
 * Format amounts without decimal places (for summary views)
 */
export const formatAmountSummary = (amount: number | string, currency = 'Ft'): string => {
  return formatAmount(amount, {currency, decimals: 0});
};

/**
 * Format numbers to only show decimals when they're not integers
 */
export const formatNumber = (value: number | string, maxDecimals = 3): string => {
  if (!value && value !== 0) return 'N/A';

  const numericValue = parseFloat(value.toString());
  if (isNaN(numericValue)) return 'N/A';

  // Check if it's an integer
  if (Number.isInteger(numericValue)) {
    return numericValue.toString();
  }

  // Format with appropriate decimal places
  return numericValue.toFixed(maxDecimals).replace(/\.?0+$/, '');
};

/**
 * Format quantity values (only show decimals when needed)
 */
export const formatQuantity = (quantity: number | string): string => {
  return formatNumber(quantity, 3);
};

/**
 * Format VAT rate percentages (only show decimals when needed)
 */
export const formatVatRate = (rate: number | string): string => {
  if (!rate && rate !== 0) return 'N/A';
  return `${formatNumber(rate, 2)}%`;
};

/**
 * Format date strings consistently
 */
export const formatDate = (dateString: string | Date): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

/**
 * Format currency amounts with proper currency-specific formatting
 */
export const formatCurrency = (amount: number | string, currencyCode: string | null = null): string => {
  if (!amount && amount !== 0) return '-';

  const numericAmount = parseFloat(amount.toString());
  if (isNaN(numericAmount)) return '-';

  // Determine currency code - use provided code or default to HUF for this Hungarian business
  const currency = currencyCode || 'HUF';

  // Currency-specific formatting configuration
  const currencyConfig: Record<string, { locale: string; minimumFractionDigits: number; maximumFractionDigits: number }> = {
    'HUF': { locale: 'hu-HU', minimumFractionDigits: 0, maximumFractionDigits: 0 },
    'EUR': { locale: 'de-DE', minimumFractionDigits: 2, maximumFractionDigits: 2 },
    'USD': { locale: 'en-US', minimumFractionDigits: 2, maximumFractionDigits: 2 },
    'GBP': { locale: 'en-GB', minimumFractionDigits: 2, maximumFractionDigits: 2 },
  };

  const config = currencyConfig[currency] || currencyConfig['HUF'];

  try {
    const formatter = new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: config.minimumFractionDigits,
      maximumFractionDigits: config.maximumFractionDigits,
    });

    return formatter.format(numericAmount);
  } catch (error) {
    // Fallback to simple formatting if currency is not supported
    const symbol = getCurrencySymbol(currency);
    return `${symbol}\u00A0${numericAmount.toLocaleString(config.locale, {
      minimumFractionDigits: config.minimumFractionDigits,
      maximumFractionDigits: config.maximumFractionDigits,
    })}`;
  }
};
